import { useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import useLayoutSize from '@/hooks/useLayoutSize';
import {
  formatInfluencerPplAmount,
  type InfluencerPplDetailInfo,
} from '@/utils/approval/influencerPpl';
import { InfluencerPplDetailRoot, InfluencerPplSheetBody } from './styles';

type DetailTableProps = {
  brand: string;
  detailInfo: InfluencerPplDetailInfo | null;
  t: TFunction;
};

/** 레거시 appr_sub_view_forms_influencer_ppl_detail_view — 중구분~지급처 명은 고정 값 */
const InfluencerPplDetailTable = ({
  brand,
  detailInfo,
  t,
}: DetailTableProps) => (
  <InfluencerPplDetailRoot>
    <table>
      <tbody>
        <tr>
          <th className="ppl-detail-head">{t('예상금액 상세내역')}</th>
        </tr>
      </tbody>
    </table>
    <table>
      <tbody>
        <tr>
          <th>{t('브랜드')}</th>
          <th>{t('중구분')}</th>
          <th>{t('소구분')}</th>
          <th>{t('항목')}</th>
          <th>{t('단가')}</th>
          <th>{t('수량')}</th>
          <th>{t('총액')}</th>
          <th>{t('금액구분')}</th>
          <th>{t('지급처코드')}</th>
          <th>{t('지급처 명')}</th>
        </tr>
        {(detailInfo?.details ?? []).map((item, i) => (
          <tr key={i}>
            <td>{brand}</td>
            <td>{t('제작비')}</td>
            <td>{t('협찬비(제품증정)')}</td>
            <td>{item.category ?? ''}</td>
            <td className="text-right">
              {formatInfluencerPplAmount(item.price)}
            </td>
            <td className="text-right">{item.qty ?? ''}</td>
            <td className="text-right">
              {formatInfluencerPplAmount(item.pricePortion)}
            </td>
            <td className="text-center">{t('현물')}</td>
            <td className="text-center">P</td>
            <td className="text-center">{t('개인')}</td>
          </tr>
        ))}
        <tr className="ppl-detail-subtotal">
          <td />
          <td>{t('소계')}</td>
          <td />
          <td />
          <td className="text-right">
            {formatInfluencerPplAmount(detailInfo?.totalPrice)}
          </td>
          <td />
          <td className="text-right">
            {formatInfluencerPplAmount(detailInfo?.totalPricePortion)}
          </td>
          <td />
          <td />
          <td />
        </tr>
        <tr className="ppl-detail-total">
          <td colSpan={4} className="text-center">
            Grand Total
          </td>
          <td />
          <td />
          <td className="text-right">
            {formatInfluencerPplAmount(detailInfo?.totalPricePortion)}
          </td>
          <td colSpan={3} />
        </tr>
      </tbody>
    </table>
  </InfluencerPplDetailRoot>
);

type DetailDialogProps = Omit<DetailTableProps, 't'> & {
  open: boolean;
  onClose: () => void;
};

/** 「상세보기」 견적 상세내역 — PC 모달, 모바일 바텀시트 */
const InfluencerPplDetailDialog = ({
  open,
  onClose,
  brand,
  detailInfo,
}: DetailDialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = (
    <InfluencerPplDetailTable brand={brand} detailInfo={detailInfo} t={t} />
  );

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={t('견적 상세내역')}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <InfluencerPplSheetBody>{body}</InfluencerPplSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={t('견적 상세내역')} handleClose={onClose}>
      {body}
    </Modal>
  );
};

export default InfluencerPplDetailDialog;
